import { ExperimentOutlined } from '@ant-design/icons'
import { Button, Input, Table } from '@surecompanies/core_components'
import { Stack } from '@sureco/design-system'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import { useDebounce } from 'hooks/useDebounce'
import Page from 'layouts/Page'
import React, { useCallback, useState } from 'react'
import { browserHistory } from 'react-router'
import {
  NEW_DEMO_COMPANY,
  demoCompanyEditPath,
  demoCompanyViewPath,
} from '../../paths'
import useListDemoCompany from './hooks/useListDemoCompany'

export default function ListDemoCompany() {
  const [pagination, setPagination] = useState({ page: 1, limit: 200 })
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearch = useDebounce(searchTerm, 300)

  const { dataSource, columns, isLoading, total } = useListDemoCompany({
    onView: (record) =>
      browserHistory.push(`/${demoCompanyViewPath(record.id)}`),
    onEdit: (record) =>
      browserHistory.push(`/${demoCompanyEditPath(record.id)}`),
    page: pagination.page,
    limit: pagination.limit,
    searchTerm: debouncedSearch,
  })

  // Any change to the search restarts at page 1. Keeping the current page
  // would request that page of the filtered results, so matches on page 1
  // would never show and the table would look empty.
  const applySearch = useCallback((value: string) => {
    setSearchTerm(value)
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }))
  }, [])

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => applySearch(e.target.value),
    [applySearch]
  )

  return (
    <Page
      title="Demo Company Management"
      breadcrumb={
        <BreadcrumbNav
          items={[{ title: 'Home', path: '/' }, { title: 'Demo Companies' }]}
        />
      }
      action={
        <Button
          type="primary"
          disabled={isLoading}
          icon={<ExperimentOutlined />}
          onClick={() => browserHistory.push(`/${NEW_DEMO_COMPANY}`)}
        >
          Create Demo Company
        </Button>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Stack direction="row" space={10} style={{ alignItems: 'center' }}>
          <Input
            placeholder="Search by company name"
            value={searchTerm}
            allowClear
            onChange={handleSearch}
            onClear={() => applySearch('')}
            style={{ maxWidth: 320 }}
          />
          <div style={{ color: '#595959', fontSize: 13 }}>
            Total: {total ?? '--'}
          </div>
        </Stack>
      </div>

      <Table
        dataSource={dataSource}
        columns={columns as any}
        rowKey={(record) => record.id}
        loading={isLoading}
        locale={{ emptyText: 'No demo companies found' }}
        sortDirections={['ascend', 'descend']}
        onRow={(record) => ({
          onClick: (e) => {
            if ((e.target as HTMLElement).closest('button')) return
            browserHistory.push(`/${demoCompanyViewPath(record.id)}`)
          },
          style: { cursor: 'pointer' },
        })}
        pagination={{
          total,
          pageSize: pagination.limit,
          current: pagination.page,
          pageSizeOptions: [25, 50, 100, 200],
          onShowSizeChange: (page, limit) =>
            setPagination((prev) => ({ ...prev, page, limit })),
          onChange: (page, limit) =>
            setPagination((prev) => ({ ...prev, page, limit })),
        }}
      />
    </Page>
  )
}
